import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { Button } from './button';
import { Check, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { departmentsAPI } from '../../services/api';

/**
 * Smart department combo input with save-as-new functionality
 * Features:
 * - Type to filter saved departments
 * - Click to select existing department
 * - Type new name and click "Save as new" to create department
 */
export function DepartmentCombo({
  value,
  onChange,
  entityId,
  departments = [],
  placeholder = "Search or create department...",
  disabled = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const [filteredDepts, setFilteredDepts] = useState(departments);
  const containerRef = useRef(null);

  // Filter departments based on input
  useEffect(() => {
    if (!inputValue.trim()) {
      setFilteredDepts(departments);
    } else {
      const filtered = departments.filter(dept =>
        dept.name.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredDepts(filtered);
    }
  }, [inputValue, departments]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selecting existing department
  const handleSelectDept = (deptName) => {
    setInputValue(deptName);
    onChange(deptName);
    setIsOpen(false);
  };

  // Handle saving as new department
  const handleSaveAsNew = async () => {
    if (!inputValue.trim()) {
      toast.error('Please enter a department name');
      return;
    }

    if (!entityId) {
      toast.error('Entity must be selected first');
      return;
    }

    try {
      setIsSaving(true);
      const response = await departmentsAPI.sync(entityId, inputValue.trim());

      // Set the saved department name (in case it was merged with existing)
      const savedName = response.data.name;
      setInputValue(savedName);
      onChange(savedName);
      setIsOpen(false);

      if (response.data.created) {
        toast.success(`Department "${savedName}" created`);
      } else {
        toast.info(`Using existing department "${savedName}"`);
      }
    } catch (error) {
      console.error('Failed to save department:', error);
      toast.error(error.response?.data?.detail || 'Failed to save department');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if input matches existing department exactly
  const exactMatch = departments.some(d => d.name.toLowerCase() === inputValue.toLowerCase());

  return (
    <div className="relative w-full" ref={containerRef}>
      <Input
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        className="pr-10"
      />

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-input rounded-md shadow-md z-50">
          {/* Existing departments */}
          {filteredDepts.length > 0 && (
            <div className="max-h-48 overflow-y-auto">
              {filteredDepts.map(dept => (
                <button
                  key={dept.id}
                  onClick={() => handleSelectDept(dept.name)}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between',
                    inputValue === dept.name && 'bg-slate-100 dark:bg-slate-800'
                  )}
                >
                  <span>{dept.name}</span>
                  {inputValue === dept.name && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Divider if showing save option */}
          {inputValue.trim() && !exactMatch && filteredDepts.length > 0 && (
            <div className="border-t border-input" />
          )}

          {/* Save as new option */}
          {inputValue.trim() && !exactMatch && (
            <button
              onClick={handleSaveAsNew}
              disabled={isSaving}
              className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 border-t border-input"
            >
              <Plus className="h-4 w-4" />
              <span>
                {isSaving ? 'Saving...' : `Save "${inputValue}" as new department`}
              </span>
            </button>
          )}

          {/* Empty state */}
          {filteredDepts.length === 0 && !inputValue.trim() && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No departments. Start typing to create one.
            </div>
          )}

          {/* No matches */}
          {filteredDepts.length === 0 && inputValue.trim() && exactMatch === false && (
            <button
              onClick={handleSaveAsNew}
              disabled={isSaving}
              className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span>
                {isSaving ? 'Saving...' : `Create "${inputValue}"`}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
